'use client';

import React, { memo, useEffect, useRef, useState } from 'react';

import { m, useInView } from 'framer-motion';

import RichText from '@/components/ui/RichText/RichText';
import { COMPONENT_STYLES } from '@/constants/styles';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import {
  StatisticComponent,
  StatisticsMetricsProps,
} from '@/types/statisticsMetrics';

import { extractNumber, extractSuffix } from './statisticsHelpers';

/**
 * Custom hook to handle the "0 to Target" count-up logic.
 * Triggered only when the component enters the viewport.
 */
const useCountUp = (end: number, isVisible: boolean, delay: number = 0) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    // If end is 0, it won't animate. We ensure 'end' gets the right data below.
    if (isVisible && end > 0 && !hasAnimated) {
      setHasAnimated(true);

      const timeout = setTimeout(() => {
        let startValue = 0;
        const duration = 2000;
        const frameRate = 1000 / 60;
        const totalFrames = Math.round(duration / frameRate);
        const increment = end / totalFrames;

        const timer = setInterval(() => {
          startValue += increment;
          if (startValue >= end) {
            setCount(end);
            clearInterval(timer);
          } else {
            setCount(Math.floor(startValue));
          }
        }, frameRate);

        return () => clearInterval(timer);
      }, delay);

      return () => clearTimeout(timeout);
    }
  }, [end, isVisible, delay, hasAnimated]);

  return count;
};

const FramerMotionStatisticCard: React.FC<{
  item: StatisticComponent;
  delay: number;
}> = memo(({ item, delay }) => {
  const label = item.subDescription?.json || item.text2 || item.title;

  const ref = useRef(null);

  const isInView = useInView(ref, { once: true, margin: '0px' });

  // This ensures if the number is in item.heading, it gets found.
  const targetNumber = extractNumber(item.description) || 0;
  const suffix = extractSuffix(item.description) || '';

  const animatedCount = useCountUp(targetNumber, isInView, delay);

  return (
    <m.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay: delay / 1000, ease: 'easeOut' }}
      className={`relative z-40 ${COMPONENT_STYLES.CARD.GLASS} flex h-full flex-col justify-center rounded-xl p-4 text-center shadow-lg transition-transform duration-300 hover:scale-105 sm:-mt-20 sm:p-6 md:-mt-24`}
    >
      <div className="mx-auto mb-2 flex w-full items-center justify-center overflow-hidden">
        <div className="text-theme-primary text-2xl leading-tight font-bold tabular-nums sm:text-3xl">
          {animatedCount.toLocaleString()}
          <span className="text-theme-primary">{suffix}</span>
        </div>
      </div>

      <div className="text-theme-primary-dark font-mulish mx-auto text-xs font-medium tracking-wide uppercase sm:text-sm">
        {label &&
          (typeof label === 'string' ? (
            <span>{label}</span>
          ) : (
            <RichText
              styles={{
                p: 'text-xs sm:text-sm font-medium uppercase tracking-wide',
              }}
            >
              {label}
            </RichText>
          ))}
      </div>
    </m.div>
  );
});

FramerMotionStatisticCard.displayName = 'FramerMotionStatisticCard';

const StatisticsMetricsFramer: React.FC<StatisticsMetricsProps> = memo(
  ({ data }) => {
    const { ref, sectionClasses, childClasses, getChildStyle } =
      useScrollAnimation();

    if (!data?.componentsCollection?.items?.length) return null;

    const statistics = data.componentsCollection.items;

    return (
      <section
        ref={ref}
        className={`relative z-30 py-6 sm:py-8 md:py-12 ${sectionClasses}`}
        data-testid="statistics-metrics-framer"
      >
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          {data.heading && (
            <m.div
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12 text-center"
            >
              {data.heading?.raw && (
                <RichText
                  styles={{
                    p: 'text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800',
                  }}
                >
                  {data.heading.raw}
                </RichText>
              )}
            </m.div>
          )}

          {/* FIX 4: Explicitly added items-stretch to the grid */}
          <div className="xs:grid-cols-2 mx-auto grid max-w-6xl grid-cols-1 items-stretch gap-3 sm:gap-4 md:grid-cols-4 md:gap-6 lg:gap-8">
            {statistics.map((item, index) => (
              <div
                key={item.sys.id}
                className={`${childClasses()} h-full`}
                style={getChildStyle(index * 100)}
              >
                <FramerMotionStatisticCard item={item} delay={index * 200} />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  },
);

StatisticsMetricsFramer.displayName = 'StatisticsMetricsFramer';

export default StatisticsMetricsFramer;
